import React from "react";
import {
  Box,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import PageDropdown from "src/pages/dashboard/prospects/PageDropdown";

const PaginationStand = ({
  loading = false,
  page,
  totalPages,
  totalRecords,
  limit,
  onPageChange,
  onLimitChange,
  showLimitSelector = true,
  RowsSelectorComponent = null,
  variant = "default",
  hideNumbers = false,
  disableNext,
}) => {
  if (loading) return null;


  const triggerPageChange = (e, value) => {
    if (!onPageChange) return;
    if (onPageChange.length === 2) {

      onPageChange(e, value);
    } else {

      onPageChange(value);
    }
  };

  const getVisiblePages = () => {
    if (!totalPages || totalPages <= 0) return [];
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    if (page <= 4) {
      return [1, 2, 3, 4, 5, "ellipsis-end", totalPages];
    }

    if (page >= totalPages - 3) {
      return [
        1,
        "ellipsis-start",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [1, "ellipsis-start", page - 1, page, page + 1, "ellipsis-end", totalPages];
  };

  if (variant === "unibox") {
    const visiblePages = getVisiblePages();

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          py: 0.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: "6px",
            gap: "14px",
            borderRadius: "8px",
          }}
        >
          <Box
            onClick={(e) => page > 1 && triggerPageChange(e, page - 1)}
            role="button"
            tabIndex={0}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: page === 1 ? "not-allowed" : "pointer",
              color: page === 1 ? "grey.400" : "text.primary",
            }}
          >
            <ChevronLeft sx={{ fontSize: "18px" }} />
          </Box>

          {!hideNumbers &&
            visiblePages.map((item, index) => {
              if (typeof item !== "number") {
                return (
                  <Typography
                    key={`${item}-${index}`}
                    sx={{
                      minWidth: "24px",
                      textAlign: "center",
                      fontSize: "20px",
                      lineHeight: "20px",
                      color: "text.secondary",
                    }}
                  >
                    ...
                  </Typography>
                );
              }

              const isActive = item === page;
              return (
                <Box
                  key={item}
                  onClick={(e) => item !== page && triggerPageChange(e, item)}
                  role="button"
                  tabIndex={0}
                  sx={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    border: isActive ? "1px solid #e2e7ed" : "1px solid transparent",
                    color: isActive ? "text.primary" : "grey.400",
                    fontSize: "16px",
                    fontWeight: isActive ? 600 : 400,
                    cursor: isActive ? "default" : "pointer",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: isActive ? "transparent" : "rgba(22, 119, 255, 0.06)",
                    },
                  }}
                >
                  {item}
                </Box>
              );
            })}

          {hideNumbers && (
            <Typography
              sx={{
                minWidth: "24px",
                textAlign: "center",
                fontSize: "13px",
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {page}
            </Typography>
          )}

          <Box
            onClick={(e) => {
              const nextDisabled = disableNext ?? page === totalPages;
              if (!nextDisabled) triggerPageChange(e, page + 1);
            }}
            role="button"
            tabIndex={0}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: (disableNext ?? page === totalPages) ? "not-allowed" : "pointer",
             // color: page === totalPages ? "#98A2B3" : "#344054",
              color: (disableNext ?? page === totalPages) ? "grey.400" : "text.primary",
            }}
          >
            <ChevronRight sx={{ fontSize: "18px" }} />
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column-reverse", sm: "row" },
        // justifyContent: "space-between",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 2,
        p: 1,
        borderTop: "1px solid rgba(224, 224, 229, 0.5)",
      }}
      className="glass-pane"
    >
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
        {/* Prev Button */}
        <Box
          onClick={(e) => page > 1 && triggerPageChange(e, page - 1)}
          role="button"
          tabIndex={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: page === 1 ? "not-allowed" : "pointer",
            color: page === 1 ? "grey.400" : "text.primary",
            // px: 1,
            // py: 0.5,
            px: 0.3,
            py: 0.15,
            borderRadius: 1,
            "&:hover": {
              backgroundColor: page === 1 ? "transparent" : "action.hover",
            },
          }}
        >
          <ChevronLeft />
        </Box>

        {/* Page Dropdown */}
        <FormControl sx={{ minWidth: 60, mx: 2, maxHeight: "40px" }}>
          <PageDropdown
            currentPage={page}
            totalPages={totalPages}
            handlePageChange={(e, value) => triggerPageChange(e, value)}
          />
        </FormControl>

        {/* Next Button */}
        <Box
          onClick={(e) => page < totalPages && triggerPageChange(e, page + 1)}
          role="button"
          tabIndex={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: page === totalPages ? "not-allowed" : "pointer",
            color: page === totalPages ? "grey.400" : "text.primary",
            // px: 1,
            // py: 0.5,
            px: 0.3,
            py: 0.15,
            borderRadius: 1,
            "&:hover": {
              backgroundColor:
                page === totalPages ? "transparent" : "action.hover",
            },
          }}
        >
          <ChevronRight />
        </Box>
      </Stack>
      {/* Range Text */}

      <Typography
        sx={{
          fontSize: "14px",
          // fontWeight: 500,
          // ml: 2.5,
          color: "text.primary",
        }}
      >
        {(() => {
          if (totalRecords === 0) {

            const start = (page - 1) * limit + 1;
            return `${start} - 0 of 0`;
          }
          const start = (page - 1) * limit + 1;
          const end = Math.min(page * limit, totalRecords);
          return `${start} - ${end} of ${new Intl.NumberFormat().format(totalRecords)}`;
        })()}
      </Typography>

      {/* </Stack> */}

      {/* Rows Per Page Selector */}

      {showLimitSelector && (
        // <Stack direction="row" justifyContent="space-between">
        <FormControl sx={{ minWidth: "100px",  }} className="glass-input">
          <InputLabel shrink>Rows</InputLabel>

          {RowsSelectorComponent ? (
            RowsSelectorComponent
          ) : (
            <Select
              value={limit}
              onChange={onLimitChange}
              size="small"
              MenuProps={{
                anchorOrigin: {
                  vertical: "top",

                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
            >
              {[25, 50, 100].map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>
        // </Stack>
      )}

    </Box>
  );
};

export default PaginationStand;
